/** Tipurile întoarse de API. Aceleași forme sunt folosite și de frontend. */

import type { AnafInfo } from './anaf';
import type { PurchaseOrder } from './po';
import type { UserSignature } from './signature';

export interface Company {
  id: string;
  name: string;
  signature: string;
  /** datele legale, pentru comenzile de achiziție; '' dacă nu sunt trecute */
  cui: string;
  regCom: string;
  address: string;
  /** ISO 8601: când s-a pus logo-ul; null = fără logo. Imaginea e la `/api/company/logo`. */
  logoAt: string | null;
  /** firmă de demonstrație: poate încărca datele demo peste ce are */
  isDemo: boolean;
  /** piața firmei (vezi markets.ts): țara ISO, moneda ISO 4217, limba */
  country: string;
  currency: string;
  locale: string;
  /** ce face firma, de la înscriere ('' = nespus) */
  activity: string;
  /** cum îi spune locului unde ajunge marfa (siteTerms.ts): 'santier' | 'locatie' | 'ferma' | 'punct' | 'proiect' */
  siteTerm: string;
  /** pachetul plătit (vezi plans.ts); null = fără pachet */
  plan: string | null;
  billing: string | null;
  /** ISO 8601: sfârșitul probei; null = firmă de dinainte de pachete */
  trialEndsAt: string | null;
  /** pachetul cerut din aplicație, cât așteaptă factura */
  planRequest: { plan: string; billing: string; at: string } | null;
  /** abonamentul plătit cu cardul (Stripe); null = fără card (factură, probă, legacy) */
  subscription: {
    /** starea din Stripe: 'active' | 'trialing' | 'past_due' | 'canceled' | … */
    status: string;
    /** anulat: merge până la `periodEnd`, apoi se oprește */
    cancelAtPeriodEnd: boolean;
    /** AAAA-LL-ZZ: următoarea plată sau sfârșitul abonamentului */
    periodEnd: string | null;
  } | null;
  /** serverul are plata cu cardul pornită (cheia Stripe pusă) */
  cardPayments: boolean;
  /** „Primii pași” ascunși de pe panou */
  onboardingHidden: boolean;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  /** 'buyer' | 'approver' (vezi roles.ts) */
  role: string;
  /** datele firmei, echipa, pachetul */
  isAdmin: boolean;
  /** ISO 8601: versiunea pozei de profil; null = fără poză */
  avatarAt?: string | null;
  /** mailul de dimineață (doar Achiziții); pornit implicit */
  morningMail?: boolean;
  /** raportul lunar pentru conducere (aprobatori și administratori); pornit implicit */
  reportMail?: boolean;
  /** limba aplicației și a mailurilor lui: 'ro' | 'en' */
  locale?: 'ro' | 'en';
  /** primește mailul de dimineață, dacă e pornit: Achiziții, sau aprobatorul singur în firmă */
  morningEligible?: boolean;
  /** semnătura din mailurile trimise în numele lui */
  signature?: UserSignature;
}

/**
 * Ce e pornit pe serverul acesta. Interfața întreabă înainte să ofere butonul:
 * mai bine nu arăți o funcție decât să o arăți și să răspundă cu o eroare.
 */
export interface Features {
  /** citirea automată a ofertelor — există cheie de API pe server */
  offerExtraction: boolean;
  /** administrator Procumi (`ADMIN_EMAILS`): face linkurile de înscriere */
  admin: boolean;
}

/** Ce întorc rutele de autentificare: cine ești, în ce firmă, ce e pornit. */
export interface Account {
  user: AuthUser;
  company: Company;
  features: Features;
}

export interface Site {
  id: string;
  name: string;
  locality: string;
  /** adresa de livrare, dacă diferă de localitate */
  address: string;
  /** beneficiarul lucrării */
  client: string;
  /** ISO 8601 dacă e închis: nu mai primește cereri noi */
  closedAt: string | null;
  /** câte cereri de ofertă are, în total și deschise */
  rfqCount: number;
  openRfqCount: number;
  /** câte oferte au fost primite pe șantier */
  offerCount: number;
  /** cât s-a comandat, în lei fără TVA: comenzile de achiziție emise, fără cele anulate */
  bought: number;
  /** bugetul de achiziții, lei fără TVA; null = nu s-a trecut */
  budget: number | null;
}

/** Șantierul, așa cum apare într-o cerere de ofertă. */
export interface SiteRef {
  id: string;
  name: string;
  locality: string;
  address: string;
}

export interface SiteInput {
  name: string;
  locality?: string;
  address?: string;
  client?: string;
  /** null = șterge bugetul */
  budget?: number | null;
}

export type SitePatch = Partial<SiteInput>;

export interface SupplierContact {
  id: string;
  /** poate fi gol, când firma are doar o adresă generică de ofertare */
  name: string;
  email: string;
  phone: string;
  /** text liber: „HVAC", „Finisaje" */
  role: string;
  /** persoana propusă implicit la o cerere nouă */
  isPrimary: boolean;
}

export interface Supplier {
  id: string;
  name: string;
  /** un distribuitor vinde mai multe lucruri */
  categories: string[];
  /** pentru comenzi; '' la furnizorii adăugați înainte să existe câmpul */
  cui: string;
  address: string;
  /** cel puțin una; prima e cea principală */
  contacts: SupplierContact[];
  /** câte cereri i-au fost trimise */
  sent: number;
  /** la câte a răspuns cu ofertă */
  answered: number;
  /** ISO 8601 dacă e arhivat: nu mai apare la cereri noi */
  archivedAt: string | null;
  /** starea la ANAF, la ultima verificare (shared/anaf.ts); null = neverificat */
  anaf: AnafInfo | null;
  /** județele în care livrează (shared/supplierProfile.ts); [] = nu s-a spus */
  counties: string[];
  /** ISO 8601: când și-a trecut furnizorul singur datele; null = niciodată */
  selfFilledAt: string | null;
  /** limba mailurilor, comenzilor și paginii ofertei: 'ro' | 'en' */
  locale: 'ro' | 'en';
}

/** Contactul principal, sau primul din listă. */
export function primaryContact(s: Supplier): SupplierContact | undefined {
  return s.contacts.find((c) => c.isPrimary) ?? s.contacts[0];
}

/** Ce s-a întâmplat la ștergerea unui furnizor. */
export interface SupplierRemoval {
  /** `deleted` dacă nu avea istoric, `archived` dacă apare în cereri trecute */
  action: 'deleted' | 'archived';
  supplier: Supplier | null;
}

export interface SupplierRef {
  id: string;
  name: string;
  /** starea la ANAF, unde contează (alegerea, comanda); lipsă = nu s-a trimis */
  anaf?: AnafInfo | null;
}

/** Persoana la care a plecat cererea. */
export interface SendContact {
  name: string;
  email: string;
}

/**
 * Caracteristicile tehnice ale unei poziții, ținute separat de descriere ca să
 * se vadă în tabel și să ajungă la citirea automată. Două înălțimi cu cantități
 * proprii sunt două poziții; mai multe înălțimi fără cantități separate rămân o
 * poziție, cu toate înălțimile păstrate.
 */
export interface ItemSpecs {
  /** înălțimea după care se ofertează, în m (la mai multe: cea mai mare) */
  height?: number | null;
  /** toate înălțimile cerute pe aceeași poziție, în m, crescător; lipsă = una singură */
  heights?: number[];
  /** grosimea plăcii sau înălțimea grinzii, în cm */
  thickness?: number | null;
}

export interface RfqItem {
  id: string;
  desc: string;
  qty: number;
  unit: string;
  /** închiriere: câte zile e nevoie de material; null sau lipsă = cumpărare */
  rentDays?: number | null;
  /** caracteristicile tehnice; lipsă = doar descrierea */
  specs?: ItemSpecs | null;
}

/** Preț unitar (lei, fără TVA) pe id de poziție. Pozițiile necotate lipsesc. */
export type OfferPrices = Record<string, number | undefined>;

/** Moneda în care a venit oferta. În tabel, totul se compară în lei. */
export type Currency = 'RON' | 'EUR';

/** O linie din detaliul unui pachet, cum apare în documentul furnizorului. */
export interface PackageLine {
  code: string;
  desc: string;
  qty: number | null;
  /** valoarea liniei, în moneda ofertei, fără TVA */
  amount: number;
}

/**
 * Oferta pe set pentru o poziție: „500 mp cofraj vertical” vine ca 33 de piese
 * (panouri, scoabe, tije) cu un singur total. Se compară totalul, nu piesele —
 * sistemele furnizorilor nu au aceleași piese. Sumele sunt în moneda ofertei.
 */
export interface OfferPackage {
  /** suma pentru tot setul, fără TVA */
  total: number;
  /** chirie: câte zile acoperă `total` (30 = pe lună, 1 = pe zi); null = vânzare */
  periodDays: number | null;
  /** chiria minimă, în zile */
  minDays: number | null;
  /** cât acoperă setul ofertat (de exemplu 450,09 mp), în unitatea poziției */
  coveredQty: number | null;
  /** costuri o singură dată: transport, montaj, curățare */
  oneTime: number;
  /** valoarea de înlocuire a materialului (cât plătești pentru ce se pierde) */
  replacement: number | null;
  /** ce spune oferta că nu include: „transport, TVA” */
  excludes: string;
  lines: PackageLine[];
}

/**
 * Transportul, cum îl spune oferta: inclus în prețuri, plătit separat sau
 * nespus ('' — și ofertele de dinainte să existe câmpul). Un preț listat mai
 * mic cu transport separat poate ieși mai scump la cost total.
 */
export type Transport = '' | 'included' | 'extra';

/** Condițiile de cantitate ale unui preț, în unitatea de măsură a poziției. */
export interface ItemTerms {
  /** comanda minimă: „minim 100 de saci” */
  minQty: number | null;
  /** se vinde doar în multipli de: „palet de 40 de saci” */
  packSize: number | null;
}

/** Un cost peste prețurile pozițiilor: descărcare cu macara, paleți, taxă de comandă mică. */
export interface OfferExtra {
  label: string;
  /** suma pentru toată comanda, în moneda ofertei, fără TVA */
  amount: number;
}

/** Avansul cerut de furnizor. Separat de termenul de plată: sunt două lucruri. */
export interface OfferAdvance {
  /** 'none' = fără avans, spus explicit; 'percent' / 'fixed' = cu valoare; 'other' = altă condiție, în text */
  kind: 'none' | 'percent' | 'fixed' | 'other';
  /** procentul (30 = 30%) sau suma fixă, în moneda ofertei; null la 'none' și 'other' */
  value: number | null;
  /** cu cuvintele ofertei: „30% la semnarea contractului” */
  text: string;
}

/** Felul unei poziții pe care furnizorul a pus-o în plus față de cerere. */
export type AdditionKind = 'accessory' | 'consumable' | 'service' | 'other';

/**
 * O poziție ofertată în plus față de cerere: console de lucru, decofrol,
 * montaj. Se arată separat și **nu intră în costul total comparat** — fiecare
 * furnizor pune alte lucruri, iar totalul n-ar mai compara același conținut.
 * Sumele sunt în moneda ofertei, fără TVA.
 */
export interface OfferAddition {
  desc: string;
  qty: number | null;
  unit: string;
  unitPrice: number | null;
  /** valoarea liniei; null dacă oferta n-o dă și nu se poate calcula */
  amount: number | null;
  kind: AdditionKind;
}

export interface Offer {
  prices: OfferPrices;
  /**
   * cât ofertează furnizorul pe poziție, când e mai puțin sau altceva decât s-a
   * cerut („avem doar 500 mp”), în unitatea poziției; lipsă = cantitatea cerută.
   * La ofertele pe set, cantitatea e `packages[id].coveredQty`.
   */
  offered?: Record<string, number | undefined>;
  /** produsul propus în loc de cel cerut, pe poziție: „echivalent Baumit” */
  alts?: Record<string, string | undefined>;
  /** avansul; lipsă = oferta nu spune */
  advance?: OfferAdvance | null;
  /** termenul de plată în zile; lipsă = nu e dat ca număr de zile */
  paymentDays?: number | null;
  /** termenul de livrare în zile; lipsă = nu se știe */
  deliveryDays?: number | null;
  /** 'AAAA-LL-ZZ': până când e valabilă oferta */
  validUntil?: string | null;
  /** 'AAAA-LL-ZZ': data de pe document */
  offerDate?: string | null;
  /** pozițiile puse în plus de furnizor */
  additions?: OfferAddition[];
  /** lipsă = RON (ofertele vechi) */
  currency?: Currency;
  /** lei pentru 1 EUR, când oferta e în EUR */
  fxRate?: number | null;
  /** ziua cursului, 'AAAA-LL-ZZ' */
  fxDate?: string | null;
  /** prețul unitar cum apare în ofertă, în moneda ei (doar când nu e RON) */
  quoted?: Record<string, number | undefined>;
  /** pozițiile ofertate pe set */
  packages?: Record<string, OfferPackage | undefined>;
  /** comanda minimă și ambalajul, pe poziție (nu la pozițiile pe set) */
  terms?: Record<string, ItemTerms | undefined>;
  /** lipsă = '' (nespus) */
  transport?: Transport;
  /** transportul separat, pentru toată comanda, în moneda ofertei; null = suma nu se știe */
  transportCost?: number | null;
  /** alte costuri pentru toată comanda, în moneda ofertei */
  extras?: OfferExtra[];
  delivery: string;
  payment: string;
  validity: string;
  notes: string;
  /** pe ce cale a venit: mail, PDF, WhatsApp, telefon */
  channel: string;
  /** textul din care au fost citite prețurile, păstrat ca sursă */
  rawText: string;
  /** ISO 8601 */
  receivedAt: string;
}

/** Document primit de la furnizor: PDF, Excel, poză de pe WhatsApp. */
export interface SendFile {
  id: string;
  /** numele pe care l-a avut la furnizor, pentru afișare */
  filename: string;
  mime: string;
  /** octeți */
  size: number;
  /** ISO 8601 */
  createdAt: string;
}

export interface Send {
  id: string;
  supplierId: string;
  supplier: SupplierRef;
  /** persoana căreia i-a fost trimisă cererea */
  contact: SendContact;
  /** ISO 8601; null până apasă omul „Am trimis” sau până îl trimite Procumi */
  sentAt: string | null;
  /** pe unde a plecat: din programul omului sau din Procumi */
  sentVia?: 'manual' | 'procumi' | null;
  /** de ce n-a plecat ultimul mail trimis din Procumi; '' = n-a fost nicio problemă */
  mailError?: string;
  /** ISO 8601: când a plecat mementoul automat; lipsă = încă nu */
  reminderAt?: string | null;
  /** ISO 8601: prima dată când furnizorul a deschis linkul de ofertă; null = încă nu */
  linkOpenedAt?: string | null;
  followUps: number;
  /** ISO 8601 sau null */
  lastFollowUp: string | null;
  /** ISO 8601: când ai trimis întrebările despre ofertă („De întrebat”); lipsă = niciodată */
  askedAt?: string | null;
  offer: Offer | null;
  /** documentele primite de la furnizor, cel mai vechi primul */
  files: SendFile[];
}

/** Un om din firmă, cât apare pe o cerere. */
export interface UserRef {
  id: string;
  name: string;
}

/** Un comentariu al echipei pe o cerere. */
export interface RfqComment {
  id: string;
  /** null = omul a plecat din firmă */
  author: (UserRef & { role: string; avatarAt: string | null }) | null;
  body: string;
  /** ISO 8601 */
  createdAt: string;
}

/** Cât poate avea un comentariu. */
export const COMMENT_MAX = 2000;

/** O notificare din clopoțel. */
export interface AppNotification {
  id: string;
  /** felul evenimentului (activity.ts) sau 'requisition_taken' */
  kind: string;
  text: string;
  /** cine a făcut-o, pentru avatar; '' = Procumi sau un furnizor */
  actorName: string;
  actorId: string | null;
  actorAvatarAt: string | null;
  rfqId: string | null;
  orderId: string | null;
  requisitionId: string | null;
  /** ISO 8601 */
  createdAt: string;
  read: boolean;
}

export interface NotificationList {
  items: AppNotification[];
  unread: number;
}

/** Cine e în firmă. */
export interface TeamMember {
  id: string;
  name: string;
  email: string;
  /** ISO 8601 dacă nu mai are voie să intre */
  deactivatedAt: string | null;
  /** tu ești ăsta */
  isYou: boolean;
  createdAt: string;
  role: string;
  isAdmin: boolean;
  /** șantierele de ale căror achiziții răspunde (primește mailurile despre comentarii) */
  siteIds: string[];
  /** ISO 8601: versiunea pozei de profil; null = fără poză */
  avatarAt: string | null;
}

/** Invitație trimisă și încă neacceptată. */
export interface PendingInvite {
  id: string;
  email: string;
  /** cine a invitat; gol dacă omul nu mai e în firmă */
  invitedBy: string;
  /** ISO 8601 */
  expiresAt: string;
  createdAt: string;
  /** cu ce rol intră */
  role: string;
  isAdmin: boolean;
}

export interface Team {
  users: TeamMember[];
  invites: PendingInvite[];
}

/**
 * Invitația proaspăt făcută. `link` se întoarce **o singură dată**: codul nu se
 * păstrează în clar, deci dacă pierzi linkul, faci alta.
 */
export interface InviteCreated {
  invite: PendingInvite;
  link: string;
  /** mailul cu invitația a plecat; false = linkul se dă de mână */
  mailed: boolean;
}

/** Ce se vede pe ecranul de acceptare, înainte să existe cont. */
export interface InviteInfo {
  companyName: string;
  email: string;
}

/**
 * Link de înscriere, cum îl vede administratorul. `new`: nedeschis sau fără
 * formular trimis; `code`: formular trimis, se așteaptă codul de pe mail;
 * `used`: firma e făcută; `expired`: n-a mai fost folosit la timp.
 */
export interface AdminSignup {
  id: string;
  note: string;
  /** emailul din formular, după ce omul l-a trimis */
  email: string | null;
  companyName: string | null;
  status: 'new' | 'code' | 'used' | 'expired';
  /** ISO 8601 */
  expiresAt: string;
  usedAt: string | null;
  createdAt: string;
}

/** O firmă, cum o vede administratorul Procumi. */
export interface AdminCompany {
  id: string;
  name: string;
  isDemo: boolean;
  /** firma administratorului: nu se poate suspenda singură */
  isYours: boolean;
  /** utilizatori activi */
  users: number;
  rfqs: number;
  /** ISO 8601 */
  createdAt: string;
  /** 'AAAA-LL-ZZ': până când e plătit; null = nu s-a trecut */
  paidUntil: string | null;
  /** ISO 8601: când i s-a oprit accesul; null = are acces */
  suspendedAt: string | null;
  /** țara ISO și răspunsurile de la înscriere ('' = nespus) */
  country: string;
  activity: string;
  buyers: string;
  plan: string | null;
  billing: string | null;
  /** 'AAAA-LL-ZZ': ultima zi de probă; null = firmă de dinainte de pachete */
  trialEndsAt: string | null;
  planRequest: { plan: string; billing: string; at: string } | null;
}

/** Ce poate schimba administratorul la o firmă. */
export interface AdminCompanyPatch {
  /** 'AAAA-LL-ZZ' sau null, ca să ștergi data */
  paidUntil?: string | null;
  suspended?: boolean;
  /** pachetul plătit; null = fără (proba sau doar citire, după `trialEndsAt`) */
  plan?: string | null;
  billing?: string | null;
  /** 'AAAA-LL-ZZ': ultima zi de probă (inclusiv) */
  trialEndsAt?: string | null;
}

/** Cât folosește firma din pachet, pentru „Contul firmei → Pachet”. */
export interface PlanUsage {
  /** utilizatori activi plus invitațiile încă bune */
  users: number;
  sites: number;
  /** documente citite de AI; limita e doar în probă (`TRIAL_AI_READS`) */
  aiReads: number;
  /** documente citite luna asta; limita lunară e a pachetului (`Plan.aiMonthly`) */
  aiMonthReads: number;
}

/** Linkul proaspăt făcut. `link` se întoarce **o singură dată**, ca la invitații. */
export interface SignupCreated {
  signup: AdminSignup;
  link: string;
}

/** Formularul de înscriere, pasul dinaintea codului de pe mail. */
export interface SignupStartInput {
  /** linkul de la Procumi; lipsește la înscrierea de pe pagina deschisă */
  token?: string;
  companyName: string;
  name: string;
  email: string;
  password: string;
  country: string;
  activity: string;
  buyers: string;
  /** termenii și politica de confidențialitate bifate (obligatoriu) */
  acceptTerms: true;
}

/**
 * Răspunsul la formular. `token` e cel de folosit la cod și la „Trimite alt
 * cod”: la înscrierea deschisă îl face serverul acum.
 */
export interface SignupStarted {
  email: string;
  token: string;
}

/** Ce arată ecranul de parolă nouă, înainte să schimbi ceva. */
export interface ResetInfo {
  email: string;
}

/**
 * Cu cine ai rămas pe o cerere. Fără asta, închiderea cererii nu spunea nimic,
 * iar scorecard-ul ghicea câștigătorul din prețuri.
 */
export interface RfqChoice {
  supplier: SupplierRef;
  /** cine a ales; null pentru cererile de dinainte de conturi multiple */
  by: UserRef | null;
  /** ISO 8601 */
  at: string;
  /** de ce el, dacă n-a fost cel mai ieftin */
  note: string;
  /** valoarea comenzii în lei fără TVA; null dacă n-ai trecut-o */
  total: number | null;
  /**
   * comanda împărțită: ce poziții ia fiecare furnizor, partea cea mai mare
   * prima (ea e și `supplier`); lipsă sau null = un singur furnizor
   */
  split?: { supplier: SupplierRef; itemIds: string[] }[] | null;
  /** totalul împărțirii la alegere, lei fără TVA: cu el trece fiecare parte pragurile de aprobare */
  splitTotal?: number | null;
}

export interface Rfq {
  id: string;
  /** 'reorder' = comandă rapidă la prețuri convenite (shared/reorder.ts), nu cerere de ofertă */
  kind?: 'rfq' | 'reorder';
  title: string;
  category: string;
  site: SiteRef;
  /** dată calendaristică, 'AAAA-LL-ZZ' */
  deadline: string;
  /** 'AAAA-LL-ZZ': când trebuie materialul pe șantier; lipsă = nu s-a spus */
  neededBy?: string | null;
  note: string;
  /** începutul mailului, scris de om; '' = textul obișnuit */
  message?: string;
  /** memento automat cu o zi înainte de termen (doar la mailurile trimise din Procumi) */
  autoRemind?: boolean;
  /** ISO 8601 dacă e închisă */
  closedAt: string | null;
  /** ISO 8601 dacă s-a renunțat la ea */
  canceledAt: string | null;
  /** ISO 8601 */
  createdAt: string;
  /** furnizorul ales, dacă ai ales unul */
  chosen: RfqChoice | null;
  /** cine a făcut cererea; null pentru cele de dinainte de conturi multiple */
  createdBy: UserRef | null;
  items: RfqItem[];
  sends: Send[];
  /** planurile și documentele cererii (shared/rfqFiles.ts); lipsă = niciunul */
  files?: SendFile[];
  /** discuția echipei despre cerere, cea mai veche întâi; furnizorii n-o văd */
  comments?: RfqComment[];
  /** comenzile de achiziție emise din cerere, cea mai nouă întâi; lipsă = niciuna */
  orders?: PurchaseOrder[];
}

/**
 * O felie dintr-o listă lungă. `next` e semnul de carte pentru urmarea ei:
 * mergem pe cursor, nu pe numărul paginii, ca o cerere nouă apărută între timp
 * să nu împingă un rând pe pagina următoare și să-l sară.
 */
export interface Page<T> {
  rows: T[];
  /** semnul de carte pentru pagina următoare; null dacă lista s-a terminat */
  next: string | null;
  /** câte intră în filtrul curent, peste toate paginile */
  total: number;
}

/** Câte cereri are fiecare stare, cu filtrele curente aplicate. */
export interface RfqCounts {
  open: number;
  closed: number;
  canceled: number;
}

export interface RfqPage extends Page<RfqListItem> {
  counts: RfqCounts;
  /** câte cereri sunt în fiecare contor de etapă (doar când s-a cerut `group`) */
  groups?: Record<string, number>;
}

/**
 * Starea unei cereri. Tipul stă aici, lângă celelalte forme de date; regula
 * după care se calculează e `rfqState()` din `status.ts`.
 */
export type RfqState = 'open' | 'closed' | 'canceled';

/** Ce se poate cere de la lista de cereri. */
export interface RfqQuery {
  state?: RfqState | 'all';
  /** contorul de etapă (vezi rfqList.ts); are întâietate față de `state` */
  group?: string;
  /** 'AAAA-LL-ZZ': ziua omului, pentru „termen depășit” */
  today?: string;
  siteId?: string;
  /** căutare liberă în titlu și categorie */
  q?: string;
  cursor?: string;
  limit?: number;
}

/** Rândul din lista de cereri: doar cât e nevoie pentru listă și indicator. */
export interface RfqListItem {
  id: string;
  title: string;
  category: string;
  site: SiteRef;
  deadline: string;
  closedAt: string | null;
  canceledAt: string | null;
  createdAt: string;
  chosen: RfqChoice | null;
  /** `avatarAt`: poza autorului, dacă și-a pus una */
  createdBy: (UserRef & { avatarAt?: string | null }) | null;
  /** `supplier`: pentru avatarele din listă */
  sends: { hasOffer: boolean; sent?: boolean; supplier?: { id: string; name: string } }[];
  /** etapa cererii (vezi rfqList.ts), când lista e cerută pe etape */
  stage?: string;
}

/* ---------- corpuri de cerere (request bodies) ---------- */

/** Ce se trimite când alegi furnizorul. */
export interface RfqChoiceInput {
  supplierId: string;
  note: string;
  /** valoarea comenzii în lei fără TVA; null dacă n-o treci */
  total: number | null;
  /** închide și cererea odată cu alegerea */
  close: boolean;
  /** comanda împărțită: ce poziții ia fiecare furnizor; lipsă = un singur furnizor */
  split?: { supplierId: string; itemIds: string[] }[] | null;
}

export interface SupplierContactInput {
  /** prezent = actualizează persoana existentă, lipsă = adaugă una nouă */
  id?: string;
  name?: string;
  email: string;
  phone?: string;
  role?: string;
  isPrimary?: boolean;
}

export interface SupplierInput {
  name: string;
  categories: string[];
  cui?: string;
  address?: string;
  /** cel puțin una */
  contacts: SupplierContactInput[];
  /** limba mailurilor către el; lipsă = a firmei */
  locale?: 'ro' | 'en';
}

/**
 * Modificare parțială: ce nu trimiți rămâne neatins. Dacă trimiți `contacts`,
 * lista devine exact ce ai trimis — persoanele lipsă sunt șterse.
 */
export type SupplierPatch = Partial<SupplierInput>;

export interface RfqRecipient {
  supplierId: string;
  /** lipsă = persoana principală a furnizorului */
  contactId?: string;
}

export interface RfqInput {
  title: string;
  category: string;
  siteId: string;
  deadline: string;
  neededBy?: string | null;
  note?: string;
  items: {
    desc: string;
    qty: number;
    unit: string;
    rentDays?: number | null;
    specs?: ItemSpecs | null;
  }[];
  recipients: RfqRecipient[];
  /** necesarele de pe șantier din care pornește cererea */
  requisitionIds?: string[];
  /** începutul mailului; gol = textul obișnuit */
  message?: string;
  autoRemind?: boolean;
}

export interface OfferInput {
  /** prețuri unitare în moneda ofertei; pozițiile din `packages` nu mai au nevoie de preț */
  prices: Record<string, number>;
  /** cantitatea ofertată, doar unde diferă de cerere */
  offered?: Record<string, number>;
  /** produsul propus în loc, doar unde e altul */
  alts?: Record<string, string>;
  advance?: OfferAdvance | null;
  paymentDays?: number | null;
  deliveryDays?: number | null;
  validUntil?: string | null;
  offerDate?: string | null;
  additions?: OfferAddition[];
  packages?: Record<string, OfferPackage>;
  currency?: Currency;
  /** obligatoriu când moneda nu e RON */
  fxRate?: number;
  fxDate?: string;
  /** comanda minimă și ambalajul, pe poziție */
  terms?: Record<string, ItemTerms>;
  transport?: Transport;
  /** doar la transport separat; lipsă = suma nu se știe */
  transportCost?: number | null;
  extras?: OfferExtra[];
  delivery?: string;
  payment?: string;
  validity?: string;
  notes?: string;
  /** pe ce cale a venit: mail, PDF, WhatsApp, telefon */
  channel?: string;
  /** textul din care au fost citite prețurile */
  rawText?: string;
}

/** Ce trimiți la citirea automată: un document urcat sau text lipit, nu ambele. */
export interface ExtractInput {
  /** id-ul unui document deja urcat la această trimitere */
  fileId?: string;
  /** textul ofertei, lipit din mail sau din WhatsApp */
  text?: string;
}

/**
 * Ce propune citirea automată. Nimic nu se salvează: prețurile intră în
 * formularul de ofertă, iar omul confirmă.
 */
export interface OfferExtraction {
  /** preț unitar pe id de poziție, doar pozițiile găsite */
  prices: Record<string, number>;
  /** pozițiile pe care oferta nu le acoperă */
  missing: string[];
  /** explicații pe poziție: conversii de unitate, echivalențe propuse */
  itemNotes: Record<string, string>;
  /** comanda minimă și ambalajul, doar pozițiile care au */
  terms: Record<string, ItemTerms>;
  transport: Transport;
  /** transportul separat, în moneda ofertei; null dacă nu e trecută suma */
  transportCost: number | null;
  /** alte costuri pentru toată comanda, în moneda ofertei */
  extras: OfferExtra[];
  delivery: string;
  payment: string;
  validity: string;
  notes: string;
  /** prețurile par să includă TVA — de verificat înainte de salvare */
  pricesIncludeVat: boolean;
  /** moneda documentului; prețurile și pachetele sunt în ea */
  currency: Currency;
  /** data ofertei din document, 'AAAA-LL-ZZ', sau '' — pentru cursul BNR */
  offerDate: string;
  /** pozițiile ofertate pe set, cu liniile documentului */
  packages: Record<string, OfferPackage>;
  /** verificările de calcul pe cifrele din document (TVA, totaluri); goale = nimic de semnalat */
  checks: string[];
  /** cantitatea ofertată, doar pozițiile unde e alta decât cea cerută */
  offered: Record<string, number>;
  /** produsul propus în loc, doar pozițiile cu echivalent */
  alts: Record<string, string>;
  advance: OfferAdvance | null;
  paymentDays: number | null;
  deliveryDays: number | null;
  validUntil: string | null;
  /** pozițiile din ofertă care nu corespund niciunei poziții cerute */
  additions: OfferAddition[];
}

/** Cursul BNR pentru o zi. */
export interface FxRate {
  currency: Currency;
  /** lei pentru o unitate */
  rate: number;
  /** ziua cursului publicat (poate fi înaintea celei cerute: weekend, sărbători) */
  date: string;
}

/** Ce întoarce urcarea unui document: documentul și cererea actualizată. */
export interface SendFileUpload {
  file: SendFile;
  rfq: Rfq;
}

export interface CompanyInput {
  name?: string;
  signature?: string;
  cui?: string;
  regCom?: string;
  address?: string;
  country?: string;
  /** ce face firma (markets.ts ACTIVITIES); '' = nespus */
  activity?: string;
  /** cum îi spune locului unde ajunge marfa (siteTerms.ts) */
  siteTerm?: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

/**
 * Pachetul a fost micșorat sub ce folosește firma (6 colegi, pachet de 2):
 * administratorul alege cine rămâne activ și ce șantiere rămân deschise.
 * null = firma încape în pachet.
 */
export interface PlanFit {
  plan: string;
  users: { limit: number; people: { id: string; name: string; email: string; isAdmin: boolean }[] } | null;
  sites: { limit: number; list: { id: string; name: string; locality: string }[] } | null;
}

/** Un document primit de la un furnizor, pe fila șantierului („Documente și oferte”). */
export interface SiteDocument {
  id: string;
  rfqId: string;
  sendId: string;
  rfqTitle: string;
  supplier: string;
  filename: string;
  mime: string;
  size: number;
  /** ISO 8601 */
  createdAt: string;
}

/** Administrare → Pâlnia: de la vizita pe site până la pachet, pe ultimele `days` zile. */
export interface AdminFunnel {
  days: number;
  site: {
    views: number;
    /** clicuri pe „Încearcă gratuit” și pe demo */
    trialClicks: number;
    demoClicks: number;
    /** paginile cele mai văzute */
    pages: { path: string; views: number }[];
    /** de unde vin oamenii; '' = direct */
    sources: { ref: string; views: number }[];
    /** țările vizitatorilor („RO”); '' = necunoscută */
    countries: { country: string; views: number }[];
  };
  /** firmele noi (fără cea de demonstrație), câte au ajuns la fiecare pas */
  steps: { key: string; label: string; companies: number }[];
}

/** Un om care are acum procumi.com deschis (harta „Acum pe site”). */
export interface LiveVisitor {
  /** codul țării, „RO”; '' = nu se știe */
  country: string;
  city: string;
  /** punctul de pe hartă; null = nu se știe unde */
  lat: number | null;
  lon: number | null;
  /** pagina deschisă acum: '/', '/en/pricing.html' */
  path: string;
  /** de unde a venit; '' = direct */
  ref: string;
  /** ISO: de când e pe site */
  since: string;
}

/** Ce s-a întâmplat pe site de curând: o pagină văzută sau un clic. */
export interface LiveEvent {
  kind: 'view' | 'trial' | 'demo';
  path: string;
  ref: string;
  country: string;
  city: string;
  lat: number | null;
  lon: number | null;
  /** ISO */
  at: string;
}

export interface AdminLive {
  /** cei care au acum o pagină deschisă (în ultimul minut) */
  visitors: LiveVisitor[];
  /** ultimele vizite și clicuri, cele mai noi întâi */
  recent: LiveEvent[];
  /** azi (ora României): afișări, clicuri „Încearcă gratuit”, țările */
  today: { views: number; trialClicks: number; countries: { country: string; views: number }[] };
}
